/**
 * Shared-компонент «Состав заказа обучения» (centrika-docs!41,
 * `component.training-order-composition`): состав заказа, сгруппированный ПО ПРОГРАММАМ.
 * У группы свои реквизиты — «Дата окончания обучения» и «Итоговый документ» (протокол об
 * обучении); дата группы «пробрасывается» в строку каждого её слушателя. Слушатели программы —
 * группами по подразделениям.
 *
 * Компоновка (ТЗ 26.10.2026, п.6.3; ответ заказчицы В24): заголовок программы — как при
 * оформлении заказа (плашка документа · краткое название · часы); итоговый документ — в строке
 * программы (у команды там же дата окончания); отдельной строки «Дата окончания обучения /
 * Итоговый документ» нет — дата есть у каждого слушателя. Клиенту строки «Состав заказа обучения ·
 * N слушателей» нет — число слушателей уже в заголовке страницы; у команды заголовок остаётся
 * (там выгрузка слушателей). Строки слушателей — плотные, загрузка документов — кнопками-значками.
 *
 * Один компонент в двух режимах (данные — один и тот же DTO, разные эндпоинты):
 *  - `team` — администратор и сотрудник Центрики: в статусе заказа «В работе» правят дату и
 *    грузят/заменяют итоговый документ; согласие ПД и документ об образовании — на просмотр;
 *  - `client` — клиент: дата и документ только на чтение («—», пока команда не заполнила),
 *    зато в строке слушателя доступно управление согласием ПД (та же ячейка, что в блоке
 *    «Документы сотрудников», — согласие живёт в карточке сотрудника, правила общие).
 *
 * Заполнение обоих реквизитов группы (дата + итоговый документ) сразу формирует записи обучения
 * слушателей в их карточках и обновляет данные заказа — без завершения всего заказа
 * (centrika-docs!60); очистка даты убирает эти записи до повторного заполнения.
 */
import { Fragment, useEffect, useState } from 'react'
import { Download, FileText } from 'lucide-react'
import {
  teamApi,
  trainingApi,
  type TrainingComposition,
  type TrainingProgramGroup,
  type TrainingTrainee,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { downloadBlob } from '@/lib/download'
import { EDUCATION_DOCUMENT_LABEL, genderLabel } from '@/lib/training'
import { compositionSummary } from '@/lib/orders'
import { groupByDepartment } from '@/lib/structure'
import { Card } from '@/design-system/components/layout/card'
import { Field } from '@/design-system/components/primitives/field'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { ProgramTitle } from '@/training/program-title'
import { Alert } from '@/design-system/components/feedback/alert'
import { SingleDocumentUpload } from '@/design-system/components/data/single-document-upload'
import { ConsentCell, EducationDocumentCell } from '@/training/employee-documents'
import { listenersCount } from '@/training/selected-programs'
import { LISTENER_STATUS_VIEW } from '@/lib/training'
import { StatusDot } from '@/design-system/components/primitives/status-dot'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

export type TrainingCompositionMode = 'team' | 'client'

const CARD_HEADER = 'Состав заказа обучения'

/**
 * Колонки таблицы слушателей программы (для colSpan групп-строк подразделений); документ об
 * образовании — колонка только у программ «ВО/СПО».
 */
function traineeColumnCount(group: TrainingProgramGroup): number {
  return group.needsEducationDocument ? 7 : 6
}

/** Имя файла выгрузки слушателей (centrika-docs!42, дословно). */
const LISTENERS_FILE_NAME = 'Список слушателей.xlsx'

/**
 * Кнопка «Скачать список слушателей» (centrika-docs!42) — команде (администратору и сотруднику
 * Центрики), режим `team`; клиенту не показывается.
 * Файл формирует сервер по согласованному шаблону; пока идёт формирование, кнопка
 * недоступна и показывает «Формирование…», при неудаче сообщение выводит карточка.
 */
function ListenersExportButton({
  orderId,
  disabled,
  onError,
}: {
  orderId: string
  disabled: boolean
  onError: (message: string | null) => void
}) {
  const [busy, setBusy] = useState(false)

  async function download() {
    onError(null)
    setBusy(true)
    try {
      downloadBlob(LISTENERS_FILE_NAME, await teamApi.training.downloadListeners(orderId))
    } catch (err) {
      onError(getErrorMessage(err, 'Не удалось скачать список слушателей'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={busy || disabled}
      onClick={() => void download()}
      leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
    >
      {busy ? 'Формирование…' : 'Скачать список слушателей'}
    </Button>
  )
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Название программы для подписей: краткое (у поданного заказа — из снимка), иначе полное. */
function programLabel(group: TrainingProgramGroup): string {
  return group.program?.shortName || group.program?.name || group.programName
}

/**
 * Итоговый документ программы в строке её названия (ТЗ п.6.3.4, ответ В24) — скачивание; пока
 * документа нет, ничего не показывается: дата окончания есть у каждого слушателя.
 */
function ProgramDocumentLink({ group }: { group: TrainingProgramGroup }) {
  if (!group.documentUrl) return null
  // Видимый текст один у всех программ — доступное имя называет программу.
  return (
    // Кнопка-ссылка: значок — внутри ссылки (Slot принимает один дочерний элемент).
    <Button asChild size="sm" variant="secondary">
      <a
        href={group.documentUrl}
        download
        title={group.documentName || undefined}
        aria-label={`Итоговый документ: ${programLabel(group)}`}
      >
        <FileText className="size-4 stroke-current" aria-hidden />
        Итоговый документ
      </a>
    </Button>
  )
}

/**
 * Реквизиты группы у команды. Дата сохраняется сразу при изменении: `DateInput` отдаёт
 * значение только когда дата разобрана целиком (или поле очищено), поэтому промежуточный
 * ввод на сервер не уходит. Итоговый документ грузится одним запросом (файл + привязка).
 */
function TeamProgramResults({
  orderId,
  group,
  canEdit,
  onChanged,
}: {
  orderId: string
  group: TrainingProgramGroup
  canEdit: boolean
  onChanged: (view: TrainingComposition) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Поколение поля даты: `DateInput` держит набранный текст во внутреннем состоянии, поэтому
  // после НЕУДАЧНОГО сохранения оно продолжало бы показывать несохранённое значение. Смена
  // ключа пересоздаёт поле с последней известной серверной датой — «не сохранилось» видно.
  const [dateKey, setDateKey] = useState(0)

  async function saveDate(iso: string) {
    setError(null)
    setBusy(true)
    try {
      onChanged(await teamApi.training.setProgramEndDate(orderId, group.programId, iso))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить дату окончания обучения'))
      setDateKey((k) => k + 1)
    } finally {
      setBusy(false)
    }
  }

  async function uploadDocument(file: File) {
    onChanged(await teamApi.training.uploadProgramDocument(orderId, group.programId, file))
  }

  if (!canEdit) return <ProgramDocumentLink group={group} />

  return (
    <div className="flex flex-wrap items-start gap-3">
      <Field label="Дата окончания обучения" required className="my-0 w-64">
        {(props) => (
          <DateInput
            {...props}
            key={dateKey}
            value={group.endDate ?? ''}
            disabled={busy}
            onChange={(iso) => void saveDate(iso)}
          />
        )}
      </Field>
      <div>
        {/* Итоговый документ — обязательный (визуально «*» у подписи), один документ на группу
            программы; удаления нет — только загрузка/замена (centrika-docs!41). */}
        <Field label="Итоговый документ" required className="my-0">
          {(props) => (
            <SingleDocumentUpload
              id={props.id}
              ariaRequired={props['aria-required']}
              ariaInvalid={props['aria-invalid']}
              ariaDescribedBy={props['aria-describedby']}
              file={
                group.documentUrl
                  ? { name: group.documentName || 'Скачать файл', downloadUrl: group.documentUrl }
                  : null
              }
              inputAriaLabel="Итоговый документ обучения"
              onUpload={uploadDocument}
              compact
              documentLabel={`Итоговый документ: ${programLabel(group)}`}
            />
          )}
        </Field>
      </div>
      {error ? (
        <Alert variant="danger" className="w-full">
          {error}
        </Alert>
      ) : null}
    </div>
  )
}

/**
 * Согласие ПД в строке слушателя: клиент управляет файлом, команда только смотрит. Согласие
 * привязано к карточке сотрудника, поэтому одинаково во всех строках человека и во всех заказах.
 */
function TraineeConsent({
  mode,
  trainee,
  onChanged,
}: {
  mode: TrainingCompositionMode
  trainee: TrainingTrainee
  onChanged: (trainee: TrainingTrainee) => void
}) {
  if (mode === 'client') {
    return (
      <ConsentCell
        employee={trainee}
        onChanged={onChanged}
        compact
        documentLabel={`Согласие ПД — ${trainee.fullName}`}
      />
    )
  }
  if (!trainee.hasConsent) return <Badge status="danger">Нет согласия</Badge>
  return trainee.consentUrl ? (
    <a className="text-13 text-accent underline" href={trainee.consentUrl} download>
      Согласие
    </a>
  ) : (
    <Badge status="success">Согласие есть</Badge>
  )
}

/**
 * Документ об образовании в строке слушателя (centrika-docs!59): клиент управляет файлом,
 * сотрудник Центрики только смотрит/скачивает. Документ привязан к карточке сотрудника, поэтому
 * его состояние одинаково во всех строках человека и во всех заказах.
 */
function TraineeEducationDocument({
  mode,
  trainee,
  onChanged,
}: {
  mode: TrainingCompositionMode
  trainee: TrainingTrainee
  onChanged: (trainee: TrainingTrainee) => void
}) {
  if (mode === 'client') {
    return (
      <EducationDocumentCell
        employee={trainee}
        onChanged={onChanged}
        compact
        documentLabel={`Документ об образовании — ${trainee.fullName}`}
      />
    )
  }
  const doc = trainee.educationDocument
  if (!doc?.hasDocument) return <Badge status="neutral">Документ не загружен</Badge>
  return doc.url ? (
    <a className="text-13 text-accent underline" href={doc.url} download>
      {doc.fileName || 'Скачать файл'}
    </a>
  ) : (
    <Badge status="success">Документ загружен</Badge>
  )
}

function ProgramGroup({
  orderId,
  mode,
  group,
  canEditResults,
  onChanged,
  onTraineeChanged,
}: {
  orderId: string
  mode: TrainingCompositionMode
  group: TrainingProgramGroup
  canEditResults: boolean
  onChanged: (view: TrainingComposition) => void
  onTraineeChanged: (trainee: TrainingTrainee) => void
}) {
  return (
    <section className="border-t border-line py-4 first:border-t-0 first:pt-0">
      {/* Строка программы: название как при оформлении заказа (п.4.3), справа — итоговый
          документ (у команды — и дата окончания обучения). */}
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        {/* Заголовок третьего уровня — только название программы (плашка и «Инфо» — рядом). */}
        <div className="min-w-0 flex-1 basis-80 text-txt">
          {group.program ? (
            <ProgramTitle program={group.program} heading />
          ) : (
            <h3 className="font-semibold">{group.programName}</h3>
          )}
          {/* Программа выбрана в пакете (training-order-packages) — пакет назван под программой. */}
          {group.packages?.length ? (
            <p className="mt-0.5 text-13 text-txt-secondary">
              {group.packages.map((name) => `В пакете «${name}»`).join(' · ')}
            </p>
          ) : null}
        </div>
        {mode === 'team' ? (
          <TeamProgramResults
            orderId={orderId}
            group={group}
            canEdit={canEditResults}
            onChanged={onChanged}
          />
        ) : (
          <ProgramDocumentLink group={group} />
        )}
      </div>
      {/* Места без названного слушателя (training-listeners-count) — у заявки «На заполнении»:
          клиент указал число, сотрудников назовёт до подтверждения данных. */}
      {group.unnamedCount ? (
        <p className="mb-3 text-13 text-amber">
          Слушатели ещё не указаны: {listenersCount(group.unnamedCount)}
        </p>
      ) : null}
      {group.trainees.length === 0 ? (
        group.unnamedCount ? null : (
          <p className="py-2 text-txt-secondary">В программе пока нет слушателей.</p>
        )
      ) : (
        // Узкий экран: прокручивается таблица, а не вся страница.
        <div className="overflow-x-auto">
          <DataTable>
            {/* Ответ заказчицы 04.11, п.11: строка заголовка — минимальной высоты; ФИО — шире,
                «Согласие ПД» и «Документ о ВО/СПО» — по содержимому. */}
            <DataTableHead>
              <DataTableRow className="[&>th]:py-1">
                <DataTableHeaderCell className="w-64">ФИО</DataTableHeaderCell>
                <DataTableHeaderCell>СНИЛС</DataTableHeaderCell>
                <DataTableHeaderCell>Должность</DataTableHeaderCell>
                <DataTableHeaderCell>Пол</DataTableHeaderCell>
                <DataTableHeaderCell className="w-px whitespace-nowrap">Согласие ПД</DataTableHeaderCell>
                {group.needsEducationDocument ? (
                  <DataTableHeaderCell className="w-px whitespace-nowrap">
                    {EDUCATION_DOCUMENT_LABEL}
                  </DataTableHeaderCell>
                ) : null}
                <DataTableHeaderCell>Дата окончания обучения</DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {groupByDepartment(group.trainees, (t) => t.departmentName).map((dep) => (
                <Fragment key={dep.department}>
                  <DataTableGroupRow colSpan={traineeColumnCount(group)}>
                    {dep.department}
                  </DataTableGroupRow>
                  {dep.items.map((t) => (
                    <DataTableRow key={t.lineId} className="[&>td]:py-2">
                      <DataTableCell className="font-medium text-txt">
                        <span className="inline-flex items-center gap-2">
                          {/* Светофор слушателя (training-listener-completeness) — с подписью для
                            чтения с экрана: не только цветом. */}
                          {t.status ? (
                            <StatusDot
                              variant={LISTENER_STATUS_VIEW[t.status].dot}
                              label={LISTENER_STATUS_VIEW[t.status].label}
                              title={LISTENER_STATUS_VIEW[t.status].label}
                            />
                          ) : null}
                          {t.fullName}
                        </span>
                      </DataTableCell>
                      {/* СНИЛС — одной строкой: перенос посреди номера читается как два значения. */}
                      <DataTableCell className="whitespace-nowrap">{t.snils || '—'}</DataTableCell>
                      <DataTableCell>{t.position || '—'}</DataTableCell>
                      <DataTableCell>{genderLabel(t.gender)}</DataTableCell>
                      <DataTableCell>
                        <TraineeConsent mode={mode} trainee={t} onChanged={onTraineeChanged} />
                      </DataTableCell>
                      {group.needsEducationDocument ? (
                        <DataTableCell>
                          <TraineeEducationDocument
                            mode={mode}
                            trainee={t}
                            onChanged={onTraineeChanged}
                          />
                        </DataTableCell>
                      ) : null}
                      {/* Дата группы «пробрасывается» в строку слушателя — отдельного значения у
                        строки нет, поэтому и правится она только у группы. */}
                      <DataTableCell>{formatDate(group.endDate)}</DataTableCell>
                    </DataTableRow>
                  ))}
                </Fragment>
              ))}
            </DataTableBody>
          </DataTable>
        </div>
      )}
    </section>
  )
}

export interface TrainingOrderCompositionProps {
  orderId: string
  mode: TrainingCompositionMode
  className?: string
  /**
   * Реквизиты программы изменились (режим команды). Готовность результатов заказа
   * (`resultsReady`, от неё зависит доступность «Завершить») живёт в карточке заказа —
   * страница перечитывает её, иначе кнопка осталась бы недоступной до перезагрузки.
   */
  onResultsChanged?: () => void
}

export function TrainingOrderComposition({
  orderId,
  mode,
  className,
  onResultsChanged,
}: TrainingOrderCompositionProps) {
  const [view, setView] = useState<TrainingComposition | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Ошибка выгрузки слушателей — отдельно от ошибки загрузки состава: она показывается
  // В КАРТОЧКЕ (требование), а не вместо неё.
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    const request =
      mode === 'team'
        ? teamApi.training.getComposition(orderId, controller.signal)
        : trainingApi.getComposition(orderId, controller.signal)
    request
      .then((res) => {
        if (!controller.signal.aborted) setView(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить состав заказа'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [orderId, mode])

  /**
   * Согласие принадлежит СОТРУДНИКУ, а не строке: у слушателя, записанного на несколько
   * программ, состояние обязано совпадать во всех его строках — обновляем все.
   */
  function updateTrainee(updated: TrainingTrainee) {
    setView((current) =>
      current
        ? {
            ...current,
            groups: current.groups.map((g) => ({
              ...g,
              trainees: g.trainees.map((t) =>
                t.personId === updated.personId
                  ? {
                      ...t,
                      hasConsent: updated.hasConsent,
                      consentFileName: updated.consentFileName,
                      consentUrl: updated.consentUrl,
                      // Документ об образовании одинаков во всех строках человека (centrika-docs!59).
                      educationDocument: updated.educationDocument,
                    }
                  : t,
              ),
            })),
          }
        : current,
    )
  }

  if (loading) {
    return (
      <Card header={mode === 'team' ? CARD_HEADER : undefined} className={className}>
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  if (error || !view) {
    return (
      <Alert variant="danger" className={className}>
        {error ?? 'Состав недоступен'}
      </Alert>
    )
  }

  // Слушатель заказа — сотрудник: записанный на несколько программ считается один раз.
  const traineeCount = new Set(view.groups.flatMap((g) => g.trainees.map((t) => t.personId))).size
  // Кнопка выгрузки недоступна, пока в заказе нет ни одного слушателя (требование).
  const hasTrainees = traineeCount > 0

  return (
    <Card
      className={className}
      // Клиенту заголовка «Состав заказа обучения · N слушателей» нет (ТЗ п.6.3.2): число
      // слушателей — в заголовке страницы. Название блока остаётся для чтения с экрана.
      role="region"
      aria-label={CARD_HEADER}
      header={
        mode === 'client' ? undefined : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <span>{CARD_HEADER}</span>
              {hasTrainees ? (
                <span className="font-normal text-txt-secondary">
                  · {compositionSummary('training', traineeCount)}
                </span>
              ) : null}
            </div>
            <ListenersExportButton
              orderId={orderId}
              disabled={!hasTrainees}
              onError={setExportError}
            />
          </div>
        )
      }
    >
      {exportError ? (
        <Alert variant="danger" className="mb-3">
          {exportError}
        </Alert>
      ) : null}
      {view.groups.length === 0 ? (
        <p className="py-2 text-txt-secondary">
          {mode === 'client'
            ? 'Сотрудники и программы пока не выбраны.'
            : 'Клиент пока не выбрал сотрудников и программы.'}
        </p>
      ) : (
        view.groups.map((g) => (
          <ProgramGroup
            key={g.programId}
            orderId={orderId}
            mode={mode}
            group={g}
            canEditResults={view.canEditResults}
            onChanged={(next) => {
              setView(next)
              onResultsChanged?.()
            }}
            onTraineeChanged={updateTrainee}
          />
        ))
      )}
    </Card>
  )
}
